import { FunctionComponent, MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BodyText, Button, Div, GridBag, Step, Steps, Title } from '@gaiads/telia-react-component-library';

import './OrganizationSetupHeader.css';
import onboardingHeaderBackground from '../../../assets/images/onboarding_header_background.svg';

type OrganizationSetupHeaderProps = {
  onboardingCurrentStepIndex: number;
  inputData: unknown;
  validateAndSaveOnboardingStepData: (data: unknown) => void;
};

const OrganizationSetupHeader: FunctionComponent<OrganizationSetupHeaderProps> = ({
  onboardingCurrentStepIndex,
  inputData,
  validateAndSaveOnboardingStepData,
}) => {
  const history = useHistory();
  const { t } = useTranslation('onboarding');

  const handleSteppingBack = (_e: MouseEvent<HTMLDivElement>, index: number) => {
    switch (index) {
      case 0:
        history.push('/onboarding/usergroups');
        break;
      case 1:
        history.push('/onboarding/users');
        break;
      case 2:
        history.push('/onboarding/groupsettings');
        break;
      case 3:
        history.push('/onboarding/approval');
        break;
    }
  };

  const isLastOnboardingStep = onboardingCurrentStepIndex === 3;

  const getHeaderContextTranslationKey = () => {
    switch (onboardingCurrentStepIndex) {
      case 0:
        return 'setupHeader.groupContext';
      case 1:
        return 'setupHeader.userContext';
      case 2:
        return 'setupHeader.groupSetupContext';
      case 3:
        return 'setupHeader.approvalContext';
      default:
        return '';
    }
  };

  return (
    <Div
      style={{
        backgroundImage: `url(${onboardingHeaderBackground})`,
        minHeight: '300px',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '50% 50%',
        height: '100%',
      }}
    >
      {/* For overflowing background color outside content container */}
      <Div className={'background-image-container-overflow'}></Div>
      <Div padding={{ top: 'xlg' }}>
        <Title size={'lg'} color={'white'}>
          {t('setupHeader.title').toLocaleUpperCase()}
        </Title>

        {/* Steps */}
        <Div margin={{ vertical: 'md' }} className={'steps'}>
          <Steps selectedStepIndex={onboardingCurrentStepIndex} onSelect={handleSteppingBack}>
            <Step label={1} description={t('setupHeader.groupCreation')} />
            <Step label={2} description={t('setupHeader.userCreation')} />
            <Step label={3} description={t('setupHeader.groupSetup')} />
            <Step label={4} description={t('setupHeader.approval')} />
          </Steps>
        </Div>

        <GridBag>
          <GridBag.Item sm={8} md={6}>
            <BodyText color={'white'}>{t(getHeaderContextTranslationKey())}</BodyText>
          </GridBag.Item>

          <GridBag.Item sm={!isLastOnboardingStep ? 0 : 1} md={3}></GridBag.Item>

          <GridBag.Item sm={!isLastOnboardingStep ? 4 : 2} md={3} style={{ textAlign: 'right' }}>
            <Button
              onClick={() => {
                validateAndSaveOnboardingStepData(inputData);
              }}
              size={'xl'}
              className={'saveAndContinueButton'}
            >
              {t(!isLastOnboardingStep ? 'setupHeader.continueButton' : 'setupHeader.finishButton')}
            </Button>
          </GridBag.Item>
        </GridBag>
      </Div>
    </Div>
  );
};

export default OrganizationSetupHeader;
